/*
*  File:            drive/slice.ts
*  Description:     Holds types and constants for Drive module
*  Author:          PreVeil
*/
import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { PaginationItem, Sort } from "@preveil-api";
import {
  InitialPaginationState, PermissionSetType, RootInfo, DirectoryEntity, DriveLimits, DirectoryPaginationInfo, CollectionInfo, GrantSetType, LinkEntity, SortType,
  SORT_DIRECTION, CollectionEntity, DRIVE_MANAGER_STATE, DriveNotifications, ACK, UploadRequest, EntryUpdate, FilesyncLocalNotificationsMap, DownloadRequest, DriveInvitationInfo,
  PreveilDownloads, DriveConstants, DriveUIActionTypes
} from "src/common";

// NOTE: Values of the DriveUIActionTypes keyMirror; keeps active_share_action tied to real action types instead of a bare string.
type DriveUIAction = keyof typeof DriveUIActionTypes;

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------
export interface DriveState {
  is_loading: boolean;
  pagination: PaginationItem,
  sort: Sort<keyof SortType>,
  selected: string[];
  default_permissions: PermissionSetType[];
  drive_state: DRIVE_MANAGER_STATE;
  default_grants: GrantSetType[];
  root_info?: RootInfo;
  current_directory?: DirectoryEntity;
  current_directory_name: string;
  current_directory_path: string;  // NOTE: Use this field to relate the current path to open the filesync directory in My PreVeil
  current_collection?: CollectionEntity;
  current_link?: LinkEntity;
  collections: CollectionInfo;
  pagination_info?: DirectoryPaginationInfo;
  notifications: DriveNotifications;
  fs_notifications: FilesyncLocalNotificationsMap;
  ack_notification?: ACK;
  upload_request?: UploadRequest;
  download_request?: DownloadRequest[];
  entry_update?: EntryUpdate;
  reload_drive_list?: boolean;
  active_share_id?: string;
  active_share_action?: DriveUIAction;
  accepted_invites?: DriveInvitationInfo[];
  gallery_index: number;
  file_name?: string;
}

// ----------------------------------------------------------------------
// Initial State and Slices 
// ----------------------------------------------------------------------
const initialState: DriveState = {
  drive_state: DRIVE_MANAGER_STATE.NOT_INITIALIZED,
  is_loading: false,
  pagination: Object.assign({}, InitialPaginationState, { pageSize: DriveLimits.DRIVE_PAGINATION_PAGE_SIZE }),
  sort: { field: "name", direction: SORT_DIRECTION.ascending },
  selected: [],
  default_permissions: [],
  default_grants: [],
  collections: {},
  notifications: {},
  fs_notifications: {},
  gallery_index: -1,
  current_directory_name: DriveConstants.ROOT_DIRECTORY,
  current_directory_path: ""
};

export const driveSlice = createSlice({
  name: "drive",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.is_loading = action.payload;
    },
    // NOTE: Only use this field for cross page corrupted issues like No permissions in db - will redirect to an error component
    setDriveState: (state, action: PayloadAction<DRIVE_MANAGER_STATE>) => {
      state.drive_state = action.payload;
    },
    setDefaultPermissions: (state, action: PayloadAction<PermissionSetType[]>) => {
      state.default_permissions = action.payload;
    },
    setDefaultGrants: (state, action: PayloadAction<GrantSetType[]>) => {
      state.default_grants = action.payload;
    },
    setRootInfoSuccess: (state, action: PayloadAction<RootInfo>) => {
      state.root_info = action.payload;
    },
    browseDirectorySuccess: (state, action: PayloadAction<DirectoryEntity>) => {
      state.current_directory = action.payload;
    },
    setCurrentDirectorySuccess: (state, action: PayloadAction<DirectoryEntity>) => {
      state.current_directory = action.payload;
    },
    resetCurrentDirectory: (state) => {
      state.current_directory = undefined;
      state.current_link = undefined;
      state.selected = [];
      state.is_loading = false;
      state.pagination = initialState.pagination;
      state.pagination_info = undefined;
      state.current_directory_path = initialState.current_directory_path;
      state.current_directory_name = initialState.current_directory_name;
    },
    setCurrentPathInfo: (state, action: PayloadAction<{ current_directory_path: string, directory_name: string }>) => {
      state.current_directory_path = action.payload.current_directory_path;
      state.current_directory_name = action.payload.directory_name;
    },
    resetCurrentPathInfo: (state) => {
      state.current_directory_path = initialState.current_directory_path;
      state.current_directory_name = initialState.current_directory_name;
    },
    setLinkEntity: (state, action: PayloadAction<LinkEntity | undefined>) => {
      state.current_link = action.payload;
    },
    setPaginationInfo: (state, action: PayloadAction<DirectoryPaginationInfo>) => {
      state.pagination_info = action.payload;
    },
    setCurrentCollection: (state, action: PayloadAction<CollectionEntity | undefined>) => {
      state.current_link = undefined;  // NOTE: Reset link to allow for a fresh link entity on new current_collection
      state.current_collection = action.payload;
    },
    setCollections: (state, action: PayloadAction<CollectionInfo>) => {
      state.collections = action.payload;
    },
    setSelected: (state, action: PayloadAction<string[]>) => {
      state.selected = action.payload;
    },
    setPagination: (state, action: PayloadAction<PaginationItem>) => {
      state.pagination = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
      state.pagination_info = undefined;
    },
    setSort: (state, action: PayloadAction<Sort<keyof SortType>>) => {
      state.sort = action.payload;
    },
    resetSort: (state) => {
      state.sort = initialState.sort;
    },
    setNotification: (state, action: PayloadAction<DriveNotifications>) => {
      state.notifications = action.payload;
    },
    setAck: (state, action: PayloadAction<ACK | undefined>) => {
      state.ack_notification = action.payload;
    },
    // Description: Local filesync notifications 
    setLocalFSNotification: (state, action: PayloadAction<FilesyncLocalNotificationsMap>) => {
      state.fs_notifications = action.payload;
    },
    setFileUploadRequest: (state, action: PayloadAction<UploadRequest | undefined>) => {
      state.upload_request = action.payload;
    },
    setFileDownloadRequest: (state, action: PayloadAction<DownloadRequest[] | undefined>) => {
      state.download_request = action.payload;
    },
    setEntryUpdate: (state, action: PayloadAction<EntryUpdate | undefined>) => {
      state.entry_update = action.payload;
    },
    setReloadDriveList: (state, action: PayloadAction<boolean>) => {
      state.reload_drive_list = action.payload;
    },
    setActiveShareId: (state, action: PayloadAction<{ id: string, action: DriveUIAction } | undefined>) => {
      state.active_share_id = action.payload?.id;
      state.active_share_action = action.payload?.action;
    },
    setAcceptedInvites: (state, action: PayloadAction<DriveInvitationInfo[]>) => {
      state.accepted_invites = action.payload;
    },
    destroyDrive: () => {
      return initialState;
    },
    setGalleryIndex: (state, action: PayloadAction<number>) => {
      state.gallery_index = action.payload;
    },
    setFileName: (state, action: PayloadAction<string>) => {
      state.file_name = action.payload;
    }
  },
});

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------
const selectFilesyncPath = (state: { drive: DriveState }) => state.drive.current_directory_path;

export const selectIsPreveilDownloadsRoot = createSelector(
  [selectFilesyncPath],
  (current_directory_path) => current_directory_path?.startsWith(`/${PreveilDownloads}`) ?? false
);


// ----------------------------------------------------------------------
// Global exports
// ----------------------------------------------------------------------
export const driveActions = driveSlice.actions;
export default driveSlice.reducer;


